"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  convertSpanishMark,
  DEFAULT_CUSTOM_CUTOFFS,
  SPANISH_SCALES,
  type SpanishBand,
  type SpanishLetterBand,
  type SpanishScale,
  type SpanishSummary,
  spanishBand,
  spanishLetterBands,
  summariseSpanish,
  validCustomCutoffs,
} from "@/lib/tool-kit/intl-grades/spanish-grades";

/** English strings. A translated content file overrides these through `ui`. */
export const SPANISH_GRADE_CONVERTER_STRINGS = {
  scaleLabel: "US conversion table",
  scales: {
    ncsu: "NC State University study abroad table (nine bands)",
    ects: "Via ECTS grades (Cantabria bands, then NC State ECTS table)",
    custom: "Custom cutoffs — type your own",
  },
  scaleHint:
    "There is no official Spain-to-US table. These are published routes that give different letters for the same mark. Use the one your receiving university states.",
  customLegend: "Lowest mark for each letter (0–10)",
  customA: "A from",
  customB: "B from",
  customC: "C from",
  customInvalid: "Cutoffs must be numbers from 0 to 10, falling from A to C.",
  coursesHeading: "Your marks out of 10",
  markLabel: "Mark /10 for course {n}",
  creditsLabel: "Credits (ECTS) for course {n}",
  mhLabel: "Matrícula de Honor (course {n}; needs 9.0 or more)",
  addCourse: "Add a course",
  removeCourse: "Remove",
  removeCourseAria: "Remove course {n}",
  includeFails: "Count failed courses (below 5) as F in the average",
  includeFailsHint:
    "Spain's own average counts only credits obtained, so this is off by default. A US evaluator may count failures.",
  results: "Result",
  average: "Weighted average /10",
  gpa: "GPA (4.0 scale)",
  band: "Qualification for the average",
  bands: {
    suspenso: "Suspenso (below 5.0)",
    aprobado: "Aprobado (5.0–6.9)",
    notable: "Notable (7.0–8.9)",
    sobresaliente: "Sobresaliente (9.0–10)",
  },
  mhCount: "Matrículas de Honor",
  creditsCounted: "Credits counted",
  creditsLeftOut: "Credits of failed courses left out: {credits}",
  tableHeading: "Course by course",
  colCourse: "Course",
  colMark: "Mark /10",
  colBand: "Qualification",
  colLetter: "Letter",
  colCredits: "Credits",
  courseName: "Course {n}",
  mhSuffix: "{mark} (MH)",
  bandName: {
    suspenso: "Suspenso",
    aprobado: "Aprobado",
    notable: "Notable",
    sobresaliente: "Sobresaliente",
  },
  skipped: "Skipped (not a mark from 0 to 10, or no credits): {rows}",
  empty: "Enter at least one passing mark (5 or more) with credits above zero.",
  tableBandsHeading: "The table in use",
  bandFrom: "From {min}",
  colFrom: "Mark from",
  colPoints: "Grade points",
  caveat:
    "Matrícula de Honor can go only to a mark of 9.0 or more, and to at most 5% of a subject's students (a single one if fewer than 20 are enrolled). The receiving university decides which table applies.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "Spanish average {average}/10 = GPA {gpa} ({band})",
} as const;

type Strings = typeof SPANISH_GRADE_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type CutoffKey = "a" | "b" | "c";

interface Row {
  id: number;
  mark: string;
  credits: string;
  mh: boolean;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, mark: "8.5", credits: "6", mh: false },
  { id: 2, mark: "7.2", credits: "6", mh: false },
  { id: 3, mark: "9.4", credits: "6", mh: true },
  { id: 4, mark: "6.1", credits: "6", mh: false },
  { id: 5, mark: "5.5", credits: "3", mh: false },
  { id: 6, mark: "4.2", credits: "6", mh: false },
];

const ONE_DECIMAL = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

function CourseRow({
  row,
  index,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <TextField
          id={`sgc-mark-${row.id}`}
          label={t(strings.markLabel, { n })}
          numeric
          onChange={(mark) => onChange({ ...row, mark })}
          value={row.mark}
        />
        <TextField
          id={`sgc-credits-${row.id}`}
          label={t(strings.creditsLabel, { n })}
          numeric
          onChange={(credits) => onChange({ ...row, credits })}
          value={row.credits}
        />
        <button
          aria-label={t(strings.removeCourseAria, { n })}
          className={BUTTON_SECONDARY}
          onClick={onRemove}
          type="button"
        >
          {strings.removeCourse}
        </button>
      </div>
      <Checkbox
        checked={row.mh}
        label={t(strings.mhLabel, { n })}
        onChange={(mh) => onChange({ ...row, mh })}
      />
    </div>
  );
}

function CustomFields({
  raw,
  valid,
  onChange,
  strings,
}: {
  raw: Record<CutoffKey, string>;
  valid: boolean;
  onChange: (key: CutoffKey, value: string) => void;
  strings: Strings;
}) {
  const fields: readonly (readonly [CutoffKey, string])[] = [
    ["a", strings.customA],
    ["b", strings.customB],
    ["c", strings.customC],
  ];
  return (
    <fieldset className="space-y-3">
      <legend className="font-medium text-foreground text-sm">
        {strings.customLegend}
      </legend>
      <div className="grid gap-3 sm:grid-cols-3">
        {fields.map(([key, label]) => (
          <TextField
            id={`sgc-custom-${key}`}
            key={key}
            label={label}
            numeric
            onChange={(value) => onChange(key, value)}
            value={raw[key]}
          />
        ))}
      </div>
      {valid ? null : <Notice>{strings.customInvalid}</Notice>}
    </fieldset>
  );
}

function ResultsSection({
  summary,
  tableRows,
  skipped,
  bands,
  strings,
  fmt,
}: {
  summary: SpanishSummary | null;
  tableRows: string[][];
  skipped: string[];
  bands: readonly SpanishLetterBand[];
  strings: Strings;
  fmt: Format;
}) {
  const averageText = summary
    ? fmt(summary.average, { maximumFractionDigits: 2 })
    : "";
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {summary === null ? (
        <Notice>{strings.empty}</Notice>
      ) : (
        <>
          <StatGrid>
            <StatCard label={strings.average} value={averageText} />
            <StatCard
              emphasis
              label={strings.gpa}
              value={fmt(summary.gpa, TWO_DECIMALS)}
            />
            <StatCard
              label={strings.band}
              value={strings.bands[summary.band]}
            />
            <StatCard label={strings.mhCount} value={fmt(summary.mhCount)} />
            <StatCard
              label={strings.creditsCounted}
              value={fmt(summary.creditsCounted, { maximumFractionDigits: 2 })}
            />
          </StatGrid>
          {summary.creditsFailed > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.creditsLeftOut, {
                credits: fmt(summary.creditsFailed, {
                  maximumFractionDigits: 2,
                }),
              })}
            </p>
          ) : null}
          {skipped.length > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.skipped, { rows: skipped.join(", ") })}
            </p>
          ) : null}
          <DataTable
            caption={strings.tableHeading}
            head={[
              strings.colCourse,
              strings.colMark,
              strings.colBand,
              strings.colLetter,
              strings.colCredits,
            ]}
            rows={tableRows}
          />
          <DataTable
            caption={strings.tableBandsHeading}
            head={[strings.colFrom, strings.colLetter, strings.colPoints]}
            rows={bands.map((band) => [
              fmt(band.min, { maximumFractionDigits: 1 }),
              band.letter,
              fmt(band.points, ONE_DECIMAL),
            ])}
          />
          <p className="text-muted-foreground text-sm">{strings.caveat}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={t(strings.summary, {
              average: averageText,
              gpa: fmt(summary.gpa, TWO_DECIMALS),
              band: strings.bandName[summary.band as SpanishBand],
            })}
          />
        </>
      )}
    </section>
  );
}

export default function SpanishGradeConverterWidget() {
  const strings = useToolUi(SPANISH_GRADE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [scale, setScale] = useState<SpanishScale>("ncsu");
  const [customRaw, setCustomRaw] = useState<Record<CutoffKey, string>>({
    a: String(DEFAULT_CUSTOM_CUTOFFS.a),
    b: String(DEFAULT_CUSTOM_CUTOFFS.b),
    c: String(DEFAULT_CUSTOM_CUTOFFS.c),
  });
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(7);
  const [includeFails, setIncludeFails] = useState(false);

  const cutoffs = {
    a: parseNumberInput(customRaw.a) ?? Number.NaN,
    b: parseNumberInput(customRaw.b) ?? Number.NaN,
    c: parseNumberInput(customRaw.c) ?? Number.NaN,
  };
  const customOk = scale !== "custom" || validCustomCutoffs(cutoffs);
  const bands = spanishLetterBands(
    scale,
    customOk ? cutoffs : DEFAULT_CUSTOM_CUTOFFS
  );

  const parsed = rows.map((row) => ({
    row,
    mark: parseNumberInput(row.mark),
    credits: parseNumberInput(row.credits),
  }));
  const courses = parsed.flatMap(({ row, mark, credits }) =>
    mark === null || credits === null ? [] : [{ mark, credits, mh: row.mh }]
  );
  const summary = customOk
    ? summariseSpanish(courses, bands, includeFails)
    : null;
  const skipped = parsed.flatMap(({ mark, credits }, index) =>
    mark !== null && credits !== null && credits > 0 && spanishBand(mark)
      ? []
      : [fmt(index + 1)]
  );

  const tableRows = parsed.flatMap(({ row, mark, credits }, index) => {
    const band = mark === null ? null : spanishBand(mark);
    const letter = mark === null ? null : convertSpanishMark(mark, bands);
    if (mark === null || credits === null || !band || !letter || credits <= 0) {
      return [];
    }
    const markText = fmt(mark, ONE_DECIMAL);
    return [
      [
        t(strings.courseName, { n: fmt(index + 1) }),
        row.mh && mark >= 9
          ? t(strings.mhSuffix, { mark: markText })
          : markText,
        strings.bandName[band],
        letter.letter,
        fmt(credits, { maximumFractionDigits: 2 }),
      ],
    ];
  });

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.scaleHint}
          id="sgc-scale"
          label={strings.scaleLabel}
          onChange={setScale}
          options={SPANISH_SCALES.map((value) => ({
            value,
            label: strings.scales[value],
          }))}
          value={scale}
        />
        {scale === "custom" ? (
          <CustomFields
            onChange={(key, value) =>
              setCustomRaw((current) => ({ ...current, [key]: value }))
            }
            raw={customRaw}
            strings={strings}
            valid={customOk}
          />
        ) : null}
        <h3 className={WIDGET_H3}>{strings.coursesHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <CourseRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, mark: "", credits: "6", mh: false },
            ]);
            setNextId((value) => value + 1);
          }}
          type="button"
        >
          {strings.addCourse}
        </button>
        <Checkbox
          checked={includeFails}
          label={strings.includeFails}
          onChange={setIncludeFails}
        />
        <p className="text-muted-foreground text-xs">
          {strings.includeFailsHint}
        </p>
      </div>
      <ResultsSection
        bands={bands}
        fmt={fmt}
        skipped={skipped}
        strings={strings}
        summary={summary}
        tableRows={tableRows}
      />
    </div>
  );
}
